import React, { useState, useEffect } from "react";
import { redirectToOAuth } from "../../utils/safeUrl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import gmailLogo from "../assets/gmail.svg";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const EmailSettings = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [gmailAccount, setGmailAccount] = useState(null);
    const [toast, setToast] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);

    const recruiterId = localStorage.getItem("org_admin_id");
    const orgId = localStorage.getItem("current_org_id");

    const fetchAccount = () => {
        if (!recruiterId || !orgId) return;
        axios
            .get(`${SOURCING_URL}/auth/email-account`, { params: { created_by: recruiterId, org_id: orgId } })
            .then(({ data }) => setGmailAccount(data.connected ? data : false))
            .catch(() => setGmailAccount(false));
    };

    useEffect(() => {
        fetchAccount();
    }, [recruiterId, orgId]);

    useEffect(() => {
        if (searchParams.get("gmail_connected") === "true") {
            setToast(true);
            setSearchParams({}, { replace: true });
            fetchAccount();
            setTimeout(() => setToast(false), 4000);
        }
    }, []);

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await axios.delete(`${SOURCING_URL}/auth/email-account/${gmailAccount.id}`);
            setGmailAccount(false);
        } catch {
            alert("Failed to disconnect. Please try again.");
        } finally {
            setDisconnecting(false);
        }
    };

    const handleConnect = async () => {
        try {
            const { data } = await axios.get(`${SOURCING_URL}/auth/gmail/connect`, {
                params: { created_by: recruiterId, org_id: orgId },
            });
            redirectToOAuth(data.auth_url);
        } catch {
            alert("Failed to start Gmail connection. Please try again.");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">
            {/* Header */}
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-base font-semibold text-gray-800 dark:text-white">Email Settings</h1>
            </div>

            {/* Success toast */}
            {toast && (
                <div className="fixed top-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-xl shadow-sm text-sm text-green-700 dark:text-green-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Gmail connected successfully!
                </div>
            )}

            <div className="max-w-2xl mx-auto px-6 py-10">
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Manage the email accounts used for outreach.</p>

                <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-gray-600 rounded-xl px-6 py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img src={gmailLogo} alt="Gmail" className="w-9 h-9 shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-gray-800 dark:text-white">Gmail</p>
                                {gmailAccount ? (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{gmailAccount.email}</p>
                                ) : (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">No account connected</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {gmailAccount === null ? null : gmailAccount ? (
                                <>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 text-xs text-green-700 dark:text-green-300 font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                        Connected
                                    </span>
                                    <button
                                        onClick={() => setShowDisconnectModal(true)}
                                        disabled={disconnecting}
                                        className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {disconnecting ? "Disconnecting…" : "Disconnect"}
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleConnect}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-white transition-all"
                                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                >
                                    Connect Gmail
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Disconnect confirmation modal */}
            {showDisconnectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-xl px-8 py-7 max-w-sm w-full mx-4">
                        <h2 className="text-base font-semibold text-gray-800 dark:text-white text-center">
                            Are you sure you want to disconnect your email?
                        </h2>
                        <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2 leading-relaxed">
                            Any active outreach currently sending from this email address will be cancelled.
                        </p>
                        <div className="flex items-center gap-3 mt-6">
                            <button
                                onClick={() => setShowDisconnectModal(false)}
                                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowDisconnectModal(false);
                                    handleDisconnect();
                                }}
                                disabled={disconnecting}
                                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-all disabled:opacity-50"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailSettings;
